import React, { useEffect } from "react";
import { Modal, Form, Input, Rate, Checkbox, Button } from "antd";
import { IReview, ReviewFormValues } from "../../../types/api/review";
import UploadImage from "../../components/UploadImage";
import { useReviewCreate, useReviewUpdate } from "../../../hooks/api/review/hooks";
import { useMessageApi } from "../../../provider/AntdNotification";

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItem?: IReview | null;
  qData?: any;
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  isOpen,
  onClose,
  selectedItem,
  qData,
}) => {
  const messageApi = useMessageApi();
  const [form] = Form.useForm<ReviewFormValues>();
  const createMutation = useReviewCreate({ qData });
  const updateMutation = useReviewUpdate({ qData });

  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (isOpen) {
      if (selectedItem) {
        form.setFieldsValue({
          name: selectedItem.name,
          role: selectedItem.role || "Customer",
          rating: selectedItem.rating || 5,
          comment: selectedItem.comment,
          avatar: selectedItem.avatar || "",
          status: selectedItem.status ?? true,
        });
      } else {
        form.setFieldsValue({
          name: "",
          role: "Customer",
          rating: 5,
          comment: "",
          avatar: "",
          status: true,
        });
      }
    }
  }, [isOpen, selectedItem, form]);

  const handleSubmit = (values: ReviewFormValues) => {
    if (selectedItem) {
      updateMutation.mutate(
        { id: selectedItem._id, data: values },
        {
          onSuccess: () => {
            messageApi.success("Review updated successfully");
            onClose();
          },
          onError: (err: any) =>
            messageApi.error(err?.response?.data?.message || err?.message || "Failed to update review"),
        }
      );
    } else {
      createMutation.mutate(values, {
        onSuccess: () => {
          messageApi.success("Review created successfully");
          onClose();
        },
        onError: (err: any) =>
          messageApi.error(err?.response?.data?.message || err?.message || "Failed to create review"),
      });
    }
  };

  return (
    <Modal
      title={selectedItem ? "Edit Review" : "Add Review"}
      open={isOpen}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isPending}
          onClick={() => form.submit()}
        >
          {selectedItem ? "Update Review" : "Save Review"}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="mt-4 flex flex-col gap-3"
      >
        <Form.Item
          name="name"
          label="Customer Name"
          rules={[{ required: true, message: "Please enter customer name" }]}
        >
          <Input placeholder="Enter customer name" />
        </Form.Item>

        <Form.Item name="role" label="Role / Designation">
          <Input placeholder="e.g. Sculpture Collector, Interior Designer" />
        </Form.Item>

        <Form.Item
          name="rating"
          label="Rating"
          rules={[{ required: true, message: "Please select rating" }]}
        >
          <Rate />
        </Form.Item>

        <Form.Item
          name="comment"
          label="Review Comment"
          rules={[{ required: true, message: "Please enter review comment" }]}
        >
          <Input.TextArea rows={4} placeholder="Enter review comment..." />
        </Form.Item>

        <Form.Item name="avatar" label="Avatar Image">
          <Form.Item name="avatar" noStyle>
            <UploadImage count={1} listType="picture-card" />
          </Form.Item>
        </Form.Item>

        <Form.Item name="status" valuePropName="checked">
          <Checkbox>Active Status</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ReviewModal;
